import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import Button from '../../Button/Button';
import Paragraph from '../../Paragraph/Paragraph';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import BasicAccountRestristionsModal from '../../Modals/BasicAccountRestrictionsModal/BasicAccountRestrictionsModal';
import IraUtmaAccountRestrictionModal from '../../Modals/IraUtmaAccountRestrictionModal/IraUtmaAccountRestrictionModal';
import NonUSAccountRestrictionModal from '../../Modals/NonUSAccountRestrictionModal/NonUSAccountRestrictionModal';
import SellFromHomeSsnLockedModal from '../../Modals/SellWizard/SellFromHomeSsnLockedModal';
import SellFromHomeWizard from '../../Modals/SellWizard/SellFromHomeWizard';
import PendingAccountVerificationModal from '../../Modals/PendingAccountVerificationModal/PendingAccountVerificationModal';
import useSellFromHomeEligibility from '../../../util/hooks/useSellFromHomeEligibility';
import useSfhGtmHook from '../../../util/hooks/useSfhGtmHook';
import {
  SFH_CTA_PORTFOLIO_CLICKED,
  SFH_VERIFY_ACCOUNT_CLICKED,
  SFH_VERIFY_ACCOUNT_DEFERRED,
} from '../../../constants/gtmEvents';
import {
  fireSfhInitiationEvent,
  resolveSfhAuthorization,
} from '../../../util/helpers/sfhGtmHelpers';
import {
  selectDefaultMailingAddress,
  selectAccountUsersData,
} from '../../../store/selectors/accountSelectors';
import { selectAuthUser } from '../../../store/selectors/userSelectors';

const SellFromHomeCtaContainer = styled.div`
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  display: flex;
  flex-direction: ${({ stacked }) => (stacked ? 'column' : 'row')};
  align-items: ${({ stacked }) => (stacked ? 'stretch' : 'center')};
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${themeColors.colorBorderPrimary};
  border-radius: 6px;
  margin-bottom: ${({ stacked }) => (stacked ? `20px` : '0px')};
  && button {
    padding: 14px;
    font-weight: 600;
  }
`;

const SellFromHomeTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SellFromHomeHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const TitleText = styled(Paragraph)`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
  color: ${themeColors.colorTextGreyDark};
`;

const SubtitleText = styled(Paragraph)`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0;
  color: ${themeColors.colorLabel};
`;

const NewBadge = styled.span`
  background-color: ${themeColors.colorNewBadge};
  color: ${themeColors.colorBlack};
  font-size: 14px;
  font-weight: 600;
  border-radius: 4px;
  padding: 4px 8px;
  white-space: nowrap;
`;

const SellFromHomeCta = ({ stacked, width, onClick }) => {
  const { t } = useTranslation();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [internalIsLockedOpen, setInternalIsLockedOpen] = useState(false);
  const {
    isGS,
    isNonUS,
    isBasicAccount,
    isIraOrUtmaAccount,
    isJointSubUser,
    isPendingAccount,
    isSellFromHomeRestricted,
  } = useSellFromHomeEligibility();
  const { sfhGtmEvent } = useSfhGtmHook();
  const defaultMailingAddress = useSelector(selectDefaultMailingAddress);
  const authUser = useSelector(selectAuthUser);
  const accountUsers = useSelector(selectAccountUsersData);
  const isControlled = typeof onClick === 'function';

  if (!isGS) return null;

  const handleCtaClick = () => {
    if (isControlled) {
      onClick();
      return;
    }
    sfhGtmEvent(SFH_CTA_PORTFOLIO_CLICKED, {
      authorization: resolveSfhAuthorization({ authUser, accountUsers }),
    });
    if (isSellFromHomeRestricted) {
      setInternalIsLockedOpen(true);
      return;
    }
    if (!isPendingAccount) {
      fireSfhInitiationEvent(sfhGtmEvent, {
        isNonUS,
        isIraOrUtmaAccount,
        isJointSubUser,
        isBasicAccount,
        country: defaultMailingAddress?.CountryIso3Code,
      });
    }
    setInternalIsOpen(true);
  };

  return (
    <>
      <SellFromHomeCtaContainer stacked={stacked} width={width}>
        <SellFromHomeTextContent>
          <SellFromHomeHeader>
            <TitleText>{t('portfolio.sellFromHome.title')}</TitleText>
            <NewBadge>{t('portfolio.sellFromHome.badge')}</NewBadge>
          </SellFromHomeHeader>
          <SubtitleText color={themeColors.colorTextSecondary}>
            {t('portfolio.sellFromHome.subtitle')}
          </SubtitleText>
        </SellFromHomeTextContent>
        <Button
          variant="primary"
          textTransform="none"
          isWidthS={stacked}
          fontSize="16px"
          lockStyle={isSellFromHomeRestricted}
          onClick={handleCtaClick}
          data-cy="button-sell-from-home"
          label={t('portfolio.sellFromHome.cta')}
        />
      </SellFromHomeCtaContainer>

      {!isControlled && (
        <>
          <SellFromHomeSsnLockedModal
            isOpen={internalIsLockedOpen}
            close={() => setInternalIsLockedOpen(false)}
          />
          {isPendingAccount && (
            <PendingAccountVerificationModal
              isOpen={internalIsOpen}
              close={() => setInternalIsOpen(false)}
            />
          )}
          {!isPendingAccount && isNonUS && (
            <NonUSAccountRestrictionModal
              isOpen={internalIsOpen}
              close={() => setInternalIsOpen(false)}
            />
          )}
          {!isPendingAccount && !isNonUS && isIraOrUtmaAccount && (
            <IraUtmaAccountRestrictionModal
              isOpen={internalIsOpen}
              close={() => setInternalIsOpen(false)}
            />
          )}
          {!isPendingAccount &&
            !isNonUS &&
            !isIraOrUtmaAccount &&
            isJointSubUser && (
              <IraUtmaAccountRestrictionModal
                isOpen={internalIsOpen}
                close={() => setInternalIsOpen(false)}
                title={t('portfolio.sellFromHome.jointSubUser.title')}
                description={t(
                  'portfolio.sellFromHome.jointSubUser.description',
                )}
                dataCyPrefix="button-sfh-joint-sub-user"
              />
            )}
          {!isNonUS &&
            !isPendingAccount &&
            !isIraOrUtmaAccount &&
            !isJointSubUser &&
            isBasicAccount && (
              <BasicAccountRestristionsModal
                isOpen={internalIsOpen}
                close={() => setInternalIsOpen(false)}
                title={t(
                  'basicAccountRestrictions.verifyAccountSellFromHome.title',
                )}
                description={t(
                  'basicAccountRestrictions.verifyAccountSellFromHome.description',
                )}
                icon={<InfoIcon width={48} height={48} />}
                showDoItLater
                onVerifyAccountClick={() =>
                  sfhGtmEvent(SFH_VERIFY_ACCOUNT_CLICKED)
                }
                onDoItLaterClick={() =>
                  sfhGtmEvent(SFH_VERIFY_ACCOUNT_DEFERRED)
                }
              />
            )}
          {!isNonUS &&
            !isPendingAccount &&
            !isIraOrUtmaAccount &&
            !isJointSubUser &&
            !isBasicAccount && (
              <SellFromHomeWizard
                isOpen={internalIsOpen}
                onClose={() => setInternalIsOpen(false)}
              />
            )}
        </>
      )}
    </>
  );
};

SellFromHomeCta.propTypes = {
  stacked: PropTypes.bool,
  width: PropTypes.number,
  onClick: PropTypes.func,
};

export default SellFromHomeCta;
